import { ReactNode } from 'react';
import { useLang } from 'hooks/lang';

import { Column, DropdownMenu, DropdownMenuItem, Row, Tooltip } from 'components/Blocks';
import { UserAvatar } from 'components/UserAvatar';
import { AttachedFiles } from 'components/Uploads';

import { PencilIcon, HistoryIcon, HorizontalDotsIcon } from 'components/Icons';

import { CommentInput } from './CommentInput';

import { useToggleV2 } from 'hooks';

import { formatPublishedAtDate, dateDistanceToNow } from 'utils/date';

import { BaseComment } from './types';

interface SingleCommentProps<Comment extends BaseComment> {
  children?: never;
  comment: Comment & { content: (string | JSX.Element)[] };
  canEdit: boolean;
  viewHistory?: () => void;
  onEdit?: (text: string, comment: Comment) => Promise<Comment | void>;

  /**
   * Add content below the regular comment box
   */
  getExtraContent?: (comment: Comment) => ReactNode;
}

export function SingleComment<Comment extends BaseComment>({
  comment,
  canEdit,
  onEdit,
  viewHistory,
  getExtraContent,
}: SingleCommentProps<Comment>) {
  const [loading, loadingHandlers] = useToggleV2();
  const [isEditing, editingHandlers] = useToggleV2();

  const { user, timestamp, content, edited, text, files, noUpdate } = comment;

  const { t } = useLang();

  const saveEdit = async (newText: string) => {
    const textToSave = newText.trim();

    if (!textToSave.trim() || textToSave === text) return editingHandlers.turnOff();

    loadingHandlers.turnOn();

    await onEdit?.(textToSave, comment);

    loadingHandlers.turnOff();
    editingHandlers.turnOff();
  };

  const canUpdate = canEdit && !noUpdate;

  const hasAction = !!(edited && viewHistory) || canUpdate;

  return (
    <Row alignItems="flex-start" className="p-2">
      <UserAvatar user={user} />

      <Column gap="1" fullWidth className="w-[calc(100%-1rem)]">
        <Row justifyContent="space-between">
          <p className="text-sm font-bold text-primary">{user.name}</p>

          <Row gap="2">
            {hasAction && (
              <DropdownMenu
                align="end"
                triggerIcon={<HorizontalDotsIcon size={14} />}
                tooltipContent={t('options')}
              >
                {edited && viewHistory && (
                  <DropdownMenuItem onClick={viewHistory}>
                    <HistoryIcon />

                    {t('view-comment-history')}
                  </DropdownMenuItem>
                )}

                {canUpdate && (
                  <DropdownMenuItem onClick={editingHandlers.turnOn}>
                    <PencilIcon />

                    {t('edit')}
                  </DropdownMenuItem>
                )}
              </DropdownMenu>
            )}

            <Tooltip content={formatPublishedAtDate(timestamp)}>
              <span className="text-xs font-medium">
                {dateDistanceToNow(timestamp)}
                {comment.edited ? '*' : ''}
              </span>
            </Tooltip>
          </Row>
        </Row>

        {isEditing ? (
          <CommentInput
            text={text}
            saveComment={saveEdit}
            loading={loading}
            close={editingHandlers.turnOff}
            allowFiles={false}
          />
        ) : (
          <div className="rounded-lg border bg-muted p-2 text-sm leading-[1.63] text-muted-foreground">
            {content}
          </div>
        )}

        {getExtraContent?.(comment)}

        <AttachedFiles files={files} />
      </Column>
    </Row>
  );
}
